import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSearch, FiX, FiChevronDown, FiChevronUp,
  FiDroplet, FiCoffee, FiBox, FiCopy, FiClock
} from 'react-icons/fi';
import ProductSearchSelect from './common/ProductSearchSelect';
import ItemSearchSelect from './common/ItemSearchSelect';

const API_URL = process.env.REACT_APP_API_URL || 'https://green-admin.merket.io/api';
const ITEMS_PER_PAGE = 20;

// Helper function to remove Vietnamese diacritics for search
const removeVietnameseDiacritics = (str) => {
  if (!str) return '';
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();
};

const RecipesManagement = () => {
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [activeTab, setActiveTab] = useState('dish');
  
  // Get user role
  const userRole = localStorage.getItem('userRole') || 'admin';
  const isSalesRole = userRole === 'sales';
  
  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  const observerTarget = useRef(null);
  const searchDebounceRef = useRef(null);
  const isFirstRender = useRef(true);
  
  const [showModal, setShowModal] = useState(false);
  const [editingRecipe, setEditingRecipe] = useState(null);
  
  const [form, setForm] = useState({
    name: '',
    type: 'dish',
    ingredients: [],
    supplies: [],
    steps: [],
    outputAmount: 0,
    outputUnit: 'ml',
    autoDiscardEndOfDay: false, // For brewing recipes - auto discard at end of day
    menuItemId: '',
    sizeOption: '', // For dish recipes - specific size option
    toppingId: '',
    notes: ''
  });
  
  // Available data
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [dishIngredients, setDishIngredients] = useState({ direct: [], brewing: [] });
  const [availableSupplies, setAvailableSupplies] = useState([]);
  const [availableToppings, setAvailableToppings] = useState([]);
  
  // Selected items for adding
  const [selectedIngredient, setSelectedIngredient] = useState({ ingredientId: '', amount: 0, unit: 'g' });
  const [selectedSupply, setSelectedSupply] = useState({ supplyId: '', amount: 1, unit: 'unit' });
  const [newStep, setNewStep] = useState({ title: '', details: '' });
  
  // Menu item search
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  // Topping search
  const [selectedTopping, setSelectedTopping] = useState(null);
  
  const [expandedId, setExpandedId] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  
  // Bulk select for mobile
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedRecipes, setSelectedRecipes] = useState([]);
  const longPressTimer = useRef(null);

  const recipeUnitLabels = { g: 'Gam', ml: 'Mililit', piece: 'Quả', unit: 'Cái' };
  const outputUnitLabels = { liter: 'Lít', ml: 'Mililit', kg: 'Kg', g: 'Gam' };

  // Debounce search
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [search]);

  // Convert decimal to fraction display for piece unit
  const formatAmount = (amount, unit) => {
    if (unit !== 'piece') return amount;
    
    const fractions = [
      { value: 0.125, label: '1/8' },
      { value: 0.143, label: '1/7' },
      { value: 0.167, label: '1/6' },
      { value: 0.2, label: '1/5' },
      { value: 0.25, label: '1/4' },
      { value: 0.333, label: '1/3' },
      { value: 0.5, label: '1/2' },
    ];
    
    // Check if it's a known fraction
    for (const f of fractions) {
      if (Math.abs(amount - f.value) < 0.01) {
        return f.label;
      }
    }
    
    // Check for whole numbers with fractions (e.g., 1.5 = 1 1/2)
    const whole = Math.floor(amount);
    const decimal = amount - whole;
    if (whole > 0 && decimal > 0.01) {
      for (const f of fractions) {
        if (Math.abs(decimal - f.value) < 0.01) {
          return `${whole} ${f.label}`;
        }
      }
    }
    
    return amount;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN').format(Math.round(amount || 0)) + 'đ';
  };

  // Load recipes with pagination
  const loadRecipes = useCallback(async (pageNum = 1, reset = false) => {
    try {
      if (reset) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      const params = {
        type: activeTab,
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        skipCount: 'true'
      };

      if (debouncedSearch) params.search = debouncedSearch;

      const response = await axios.get(`${API_URL}/recipes`, { params });
      const { data, pagination } = response.data;

      if (reset || pageNum === 1) {
        setRecipes(data || []);
      } else {
        setRecipes(prev => [...prev, ...(data || [])]);
      }

      setPage(pageNum);
      setHasMore(pagination?.hasMore || false);
    } catch (error) {
      toast.error('Không thể tải danh sách công thức');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeTab, debouncedSearch]);

  // Load more items
  const loadMoreItems = useCallback(() => {
    if (!hasMore || loadingMore) return;
    loadRecipes(page + 1, false);
  }, [hasMore, loadingMore, page, loadRecipes]);

  // Initial load
  useEffect(() => {
    loadRecipes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reload when search or tab changes
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    loadRecipes(1, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, activeTab]);

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, activeTab]);

  // IntersectionObserver for infinite scroll
  useEffect(() => {
    const target = observerTarget.current;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [hasMore, loadingMore, loading, loadMoreItems]);

  useEffect(() => {
    if (showModal) {
      if (form.type === 'brewing') {
        loadBrewingIngredients();
      } else if (form.type === 'dish') {
        loadDishIngredients();
        loadSupplies();
      } else if (form.type === 'topping') {
        loadDishIngredients();
        loadSupplies();
        loadToppings();
      }
    }
  }, [showModal, form.type]);

  const loadBrewingIngredients = async () => {
    try {
      const response = await axios.get(`${API_URL}/recipes/ingredients/for-brewing`);
      setAvailableIngredients(response.data.data || []);
    } catch (error) {
      console.error('Error loading brewing ingredients:', error);
    }
  };

  const loadDishIngredients = async () => {
    try {
      const response = await axios.get(`${API_URL}/recipes/ingredients/for-dish`);
      setDishIngredients(response.data.data || { direct: [], brewing: [] });
    } catch (error) {
      console.error('Error loading dish ingredients:', error);
    }
  };

  const loadSupplies = async () => {
    try {
      const response = await axios.get(`${API_URL}/recipes/supplies/for-dish`);
      setAvailableSupplies(response.data.data || []);
    } catch (error) {
      console.error('Error loading supplies:', error);
    }
  };

  const loadToppings = async () => {
    try {
      const response = await axios.get(`${API_URL}/toppings/active`);
      setAvailableToppings(response.data.data || []);
    } catch (error) {
      console.error('Error loading toppings:', error);
    }
  };

  const openModal = (recipe = null) => {
    if (recipe) {
      setEditingRecipe(recipe);
      setForm({
        name: recipe.name,
        type: recipe.type,
        ingredients: recipe.ingredients.map(ing => ({
          ingredientId: ing.ingredientId?._id || ing.ingredientId,
          ingredientName: ing.ingredientName || ing.ingredientId?.name,
          amount: ing.amount,
          unit: ing.unit
        })),
        supplies: (recipe.supplies || []).map(sup => ({
          supplyId: sup.supplyId?._id || sup.supplyId,
          supplyName: sup.supplyName || sup.supplyId?.name,
          amount: sup.amount,
          unit: sup.unit
        })),
        steps: recipe.steps.map(s => ({
          title: s.title || s.description || '',
          details: s.details || ''
        })),
        outputAmount: recipe.outputAmount || 0,
        outputUnit: recipe.outputUnit || 'ml',
        autoDiscardEndOfDay: recipe.resultIngredientId?.autoDiscardEndOfDay || false,
        menuItemId: recipe.menuItemId?._id || recipe.menuItemId || '',
        sizeOption: recipe.sizeOption || '',
        toppingId: recipe.toppingId?._id || recipe.toppingId || '',
        notes: recipe.notes || ''
      });
      // Set menu item search state when editing
      if (recipe.menuItemId) {
        setSelectedMenuItem(recipe.menuItemId);
      } else {
        setSelectedMenuItem(null);
      }
      // Set topping search state when editing
      if (recipe.toppingId) {
        setSelectedTopping(recipe.toppingId);
      } else {
        setSelectedTopping(null);
      }
    } else {
      setEditingRecipe(null);
      setForm({
        name: '',
        type: activeTab,
        ingredients: [],
        supplies: [],
        steps: [],
        outputAmount: 0,
        outputUnit: 'ml',
        autoDiscardEndOfDay: false,
        menuItemId: '',
        sizeOption: '',
        toppingId: '',
        notes: ''
      });
      setSelectedMenuItem(null);
      setSelectedTopping(null);
    }
    setSelectedIngredient({ ingredientId: '', amount: 0, unit: 'g' });
    setSelectedSupply({ supplyId: '', amount: 1, unit: 'unit' });
    setNewStep({ title: '', details: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingRecipe(null);
    setSelectedMenuItem(null);
    setSelectedTopping(null);
  };

  const addIngredient = () => {
    if (!selectedIngredient.ingredientId || selectedIngredient.amount <= 0) {
      toast.error('Vui lòng chọn nguyên liệu và nhập số lượng');
      return;
    }

    let ingredientName = '';
    if (form.type === 'brewing') {
      const ing = availableIngredients.find(i => i._id === selectedIngredient.ingredientId);
      ingredientName = ing?.name || '';
    } else {
      const directIng = dishIngredients.direct.find(i => i._id === selectedIngredient.ingredientId);
      const brewingIng = dishIngredients.brewing.find(i => i._id === selectedIngredient.ingredientId);
      ingredientName = directIng?.name || brewingIng?.name || '';
    }

    if (form.ingredients.find(i => i.ingredientId === selectedIngredient.ingredientId)) {
      toast.error('Nguyên liệu này đã được thêm');
      return;
    }

    setForm(prev => ({
      ...prev,
      ingredients: [...prev.ingredients, {
        ingredientId: selectedIngredient.ingredientId,
        ingredientName,
        amount: selectedIngredient.amount,
        unit: selectedIngredient.unit
      }]
    }));
    setSelectedIngredient({ ingredientId: '', amount: 0, unit: 'g' });
  };

  const removeIngredient = (index) => {
    setForm(prev => ({
      ...prev,
      ingredients: prev.ingredients.filter((_, i) => i !== index)
    }));
  };

  const addSupply = () => {
    if (!selectedSupply.supplyId || selectedSupply.amount <= 0) {
      toast.error('Vui lòng chọn vật dụng và nhập số lượng');
      return;
    }

    const supply = availableSupplies.find(s => s._id === selectedSupply.supplyId);
    
    if (form.supplies.find(s => s.supplyId === selectedSupply.supplyId)) {
      toast.error('Vật dụng này đã được thêm');
      return;
    }

    setForm(prev => ({
      ...prev,
      supplies: [...prev.supplies, {
        supplyId: selectedSupply.supplyId,
        supplyName: supply?.name || '',
        amount: selectedSupply.amount,
        unit: selectedSupply.unit
      }]
    }));
    setSelectedSupply({ supplyId: '', amount: 1, unit: 'unit' });
  };

  const removeSupply = (index) => {
    setForm(prev => ({
      ...prev,
      supplies: prev.supplies.filter((_, i) => i !== index)
    }));
  };

  const addStep = () => {
    if (!newStep.title.trim()) {
      toast.error('Vui lòng nhập tên bước chế biến');
      return;
    }
    setForm(prev => ({
      ...prev,
      steps: [...prev.steps, { title: newStep.title.trim(), details: newStep.details.trim() }]
    }));
    setNewStep({ title: '', details: '' });
  };

  const removeStep = (index) => {
    setForm(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const moveStep = (index, direction) => {
    const newSteps = [...form.steps];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newSteps.length) return;
    [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
    setForm(prev => ({ ...prev, steps: newSteps }));
  };

  const getIngredientUnit = (ingredientId) => {
    let ing = null;
    
    if (form.type === 'brewing') {
      ing = availableIngredients.find(i => i._id === ingredientId);
    } else {
      ing = dishIngredients.direct.find(i => i._id === ingredientId) 
         || dishIngredients.brewing.find(i => i._id === ingredientId);
    }
    
    if (!ing) return 'g';
    
    // If recipeUnit is set, use it
    if (ing.recipeUnit) return ing.recipeUnit;
    
    // Auto-detect based on packagingUnit/measurementUnit for legacy data
    if (ing.packagingUnit === 'piece' || ing.measurementUnit === 'piece') {
      return 'piece';
    }
    if (ing.packagingUnit === 'item') {
      return 'unit';
    }
    
    // Fallback to measurementUnit or default 'g'
    if (ing.measurementUnit === 'kg' || ing.measurementUnit === 'g') {
      return 'g';
    }
    if (ing.measurementUnit === 'liter' || ing.measurementUnit === 'ml') {
      return 'ml';
    }
    
    return 'g';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim()) {
      toast.error('Vui lòng nhập tên công thức');
      return;
    }
    
    if (form.ingredients.length === 0) {
      toast.error('Vui lòng thêm ít nhất 1 nguyên liệu');
      return;
    }
    
    if (form.type === 'brewing' && form.outputAmount <= 0) {
      toast.error('Vui lòng nhập số lượng thành phẩm');
      return;
    }

    try {
      if (editingRecipe) {
        await axios.put(`${API_URL}/recipes/${editingRecipe._id}`, form);
      } else {
        await axios.post(`${API_URL}/recipes`, form);
      }
      closeModal();
      loadRecipes(1, true);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Có lỗi xảy ra');
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/recipes/${id}`);
      setShowDeleteConfirm(null);
      loadRecipes(1, true);
    } catch (error) {
      toast.error('Không thể xóa công thức');
    }
  };

  const handleDuplicate = async (recipe) => {
    try {
      // Prepare duplicate data
      const duplicateData = {
        name: `${recipe.name} (copy)`,
        type: recipe.type,
        ingredients: recipe.ingredients.map(ing => ({
          ingredientId: ing.ingredientId?._id || ing.ingredientId,
          amount: ing.amount,
          unit: ing.unit
        })),
        supplies: (recipe.supplies || []).map(sup => ({
          supplyId: sup.supplyId?._id || sup.supplyId,
          amount: sup.amount,
          unit: sup.unit
        })),
        steps: recipe.steps.map(s => ({
          title: s.title || '',
          details: s.details || ''
        })),
        outputAmount: recipe.outputAmount || 0,
        outputUnit: recipe.outputUnit || 'ml',
        menuItemId: recipe.menuItemId?._id || recipe.menuItemId || '',
        sizeOption: recipe.sizeOption || '',
        toppingId: recipe.toppingId?._id || recipe.toppingId || '',
        notes: recipe.notes || ''
      };

      // Create duplicate via API
      const response = await axios.post(`${API_URL}/recipes`, duplicateData);
      
      if (response.data.success) {
        await loadRecipes(1, true);
        
        // Open edit modal with the new recipe
        const newRecipe = response.data.data;
        openModal(newRecipe);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Không thể sao chép công thức');
    }
  };

  // Simple local filtering for immediate feedback
  const filteredRecipes = recipes.filter(r => {
    if (!debouncedSearch) return true;
    const searchLower = debouncedSearch.toLowerCase();
    const searchNormalized = removeVietnameseDiacritics(debouncedSearch);
    const nameLower = (r.name || '').toLowerCase();
    const nameNormalized = removeVietnameseDiacritics(r.name);
    return nameLower.includes(searchLower) || nameNormalized.includes(searchNormalized);
  });

  // Bulk select handlers
  const toggleSelectRecipe = (recipeId) => {
    setSelectedRecipes(prev => 
      prev.includes(recipeId) 
        ? prev.filter(id => id !== recipeId)
        : [...prev, recipeId]
    );
  };

  const exitBulkSelectMode = () => {
    setBulkSelectMode(false);
    setSelectedRecipes([]);
  };

  const handleBulkDelete = async () => {
    if (selectedRecipes.length === 0) return;
    
    if (!window.confirm(`Bạn có chắc muốn xóa ${selectedRecipes.length} công thức đã chọn?`)) {
      return;
    }

    try {
      await Promise.all(selectedRecipes.map(id => 
        axios.delete(`${API_URL}/recipes/${id}`)
      ));
      exitBulkSelectMode();
      loadRecipes(1, true);
    } catch (error) {
      toast.error('Có lỗi khi xóa công thức');
    }
  };

  const handleLongPressStart = (recipeId) => {
    longPressTimer.current = setTimeout(() => {
      setBulkSelectMode(true);
      setSelectedRecipes([recipeId]);
    }, 500);
  };

  const handleLongPressEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  const handleCardClick = (recipe) => {
    if (bulkSelectMode) {
      toggleSelectRecipe(recipe._id);
    } else {
      setExpandedId(expandedId === recipe._id ? null : recipe._id);
    }
  };

  return (
    <div className="space-y-4 pb-16 md:pb-0">
      {/* Tabs */}
      <div className="flex gap-1 md:gap-2 border-b overflow-x-auto">
        <button
          onClick={() => setActiveTab('dish')}
          className={`px-3 md:px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm md:text-base ${
            activeTab === 'dish' 
              ? 'border-green-500 text-green-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FiCoffee className="inline mr-1 md:mr-2" />
          Món
        </button>
        <button
          onClick={() => setActiveTab('topping')}
          className={`px-3 md:px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm md:text-base ${
            activeTab === 'topping' 
              ? 'border-amber-500 text-amber-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FiPlus className="inline mr-1 md:mr-2" />
          Topping
        </button>
        <button
          onClick={() => setActiveTab('brewing')}
          className={`px-3 md:px-4 py-2 font-medium border-b-2 transition-colors whitespace-nowrap text-sm md:text-base ${
            activeTab === 'brewing' 
              ? 'border-blue-500 text-blue-600' 
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <FiDroplet className="inline mr-1 md:mr-2" />
          Ủ
        </button>
      </div>

      {/* Header */}
      {/* Mobile: Bulk Select Mode Header */}
      {bulkSelectMode && (
        <div className="sm:hidden bg-red-500 text-white px-4 py-3 flex items-center justify-between rounded-lg">
          <div className="flex items-center space-x-3">
            <button onClick={exitBulkSelectMode} className="p-1">
              <FiX size={20} />
            </button>
            <span className="font-medium">{selectedRecipes.length} đã chọn</span>
          </div>
          <button
            onClick={handleBulkDelete}
            disabled={selectedRecipes.length === 0}
            className="p-2 rounded-full bg-white/20 disabled:opacity-50"
          >
            <FiTrash2 size={18} />
          </button>
        </div>
      )}
      
      {!bulkSelectMode && (
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Tìm công thức..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-green-500 text-sm md:text-base"
          />
        </div>
        <div className="flex items-center gap-2">
          {activeTab === 'brewing' && (
            <Link
              to="/inventory/ingredients?tab=logs"
              className="hidden sm:flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FiClock /> Lịch sử ủ
            </Link>
          )}
          {activeTab === 'brewing' && (
            <Link
              to="/inventory/ingredients?tab=logs"
              className="sm:hidden p-2 bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
            >
              <FiClock size={18} />
            </Link>
          )}
          <button
            onClick={() => openModal()}
            className="hidden sm:flex items-center gap-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            <FiPlus /> Thêm công thức
          </button>
          <button
            onClick={() => openModal()}
            className="sm:hidden p-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
          >
            <FiPlus size={18} />
          </button>
        </div>
      </div>
      )}

      {/* Recipe List */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
          </div>
        ) : filteredRecipes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            Chưa có công thức {activeTab === 'brewing' ? 'ủ' : activeTab === 'topping' ? 'topping' : 'món'} nào
          </div>
        ) : (
          filteredRecipes.map(recipe => (
            <div 
              key={recipe._id} 
              className={`bg-white rounded-lg shadow border overflow-hidden relative ${
                bulkSelectMode && selectedRecipes.includes(recipe._id) ? 'ring-2 ring-red-500' : ''
              }`}
              onTouchStart={() => handleLongPressStart(recipe._id)}
              onTouchEnd={handleLongPressEnd}
              onTouchCancel={handleLongPressEnd}
              onMouseDown={() => handleLongPressStart(recipe._id)}
              onMouseUp={handleLongPressEnd}
              onMouseLeave={handleLongPressEnd}
            >
              {/* Mobile: absolute positioned icons - hide delete, show checkbox in bulk mode */}
              <div className="sm:hidden absolute top-2 right-2 flex flex-col gap-0.5 z-10">
                {bulkSelectMode ? (
                  <div 
                    className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      selectedRecipes.includes(recipe._id) 
                        ? 'bg-red-500 border-red-500 text-white' 
                        : 'border-gray-300 bg-white'
                    }`}
                    onClick={(e) => { e.stopPropagation(); toggleSelectRecipe(recipe._id); }}
                  >
                    {selectedRecipes.includes(recipe._id) && <span className="text-xs">✓</span>}
                  </div>
                ) : (
                  <>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDuplicate(recipe); }}
                      className="p-1.5 text-gray-500 hover:bg-gray-100 rounded"
                    >
                      <FiCopy size={16} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(recipe); }}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                    >
                      <FiEdit2 size={16} />
                    </button>
                  </>
                )}
              </div>

              {/* Recipe Header */}
              <div 
                className="p-4 sm:pr-4 pr-14 cursor-pointer hover:bg-gray-50"
                onClick={() => handleCardClick(recipe)}
              >
                <div className="flex items-start gap-3">
                  {/* Show menu item image for dish recipes, otherwise show icon */}
                  {activeTab === 'dish' && recipe.menuItemId?.image ? (
                    <div className="w-10 h-10 rounded-lg overflow-hidden flex-shrink-0">
                      <img 
                        src={recipe.menuItemId.image} 
                        alt={recipe.menuItemId.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                      activeTab === 'brewing' ? 'bg-purple-100' : 
                      activeTab === 'topping' ? 'bg-amber-100' : 'bg-orange-100'
                    }`}>
                      {activeTab === 'brewing' ? <FiDroplet className="text-purple-600" /> : 
                       activeTab === 'topping' ? <FiPlus className="text-amber-600" /> : 
                       <FiCoffee className="text-orange-600" />}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium truncate">{recipe.name}</h3>
                      {/* Desktop only: show linked item name */}
                      <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
                        {recipe.menuItemId && (
                          <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded whitespace-nowrap">
                            {recipe.menuItemId.name}
                          </span>
                        )}
                        {recipe.sizeOption && (
                          <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded whitespace-nowrap">
                            {recipe.sizeOption}
                          </span>
                        )}
                        {recipe.toppingId && (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded whitespace-nowrap">
                            {recipe.toppingId.name}
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">
                      {recipe.ingredients.length} nguyên liệu
                      {recipe.supplies?.length > 0 && `, ${recipe.supplies.length} vật dụng`}
                      {recipe.type === 'brewing' && recipe.outputAmount > 0 && (
                        <span className="ml-2 text-purple-600">
                          → {recipe.outputAmount} {outputUnitLabels[recipe.outputUnit]}
                        </span>
                      )}
                    </p>
                  </div>
                  
                  {/* Desktop: Cost & Profit for dish recipes */}
                  {activeTab === 'dish' && (
                    <div className="hidden sm:flex items-center gap-4 mr-4">
                      {!isSalesRole && (
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Chi phí</div>
                          <div className="font-medium text-orange-600">
                            {formatCurrency(recipe.calculatedCost)}
                          </div>
                        </div>
                      )}
                      {recipe.menuItemId && !isSalesRole && (
                        <>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Giá bán</div>
                            <div className="font-medium">
                              {formatCurrency(recipe.sellPrice || recipe.menuItemId.price)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Lãi</div>
                            <div className={`font-medium ${recipe.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(recipe.profit)}
                              <span className="text-xs ml-1">
                                ({recipe.profitMargin?.toFixed(0)}%)
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Desktop: Cost & Profit for topping recipes */}
                  {activeTab === 'topping' && (
                    <div className="hidden sm:flex items-center gap-4 mr-4">
                      {!isSalesRole && (
                        <div className="text-right">
                          <div className="text-xs text-gray-500">Chi phí</div>
                          <div className="font-medium text-orange-600">
                            {formatCurrency(recipe.calculatedCost)}
                          </div>
                        </div>
                      )}
                      {recipe.toppingId && !isSalesRole && (
                        <>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Giá bán</div>
                            <div className="font-medium">
                              {formatCurrency(recipe.toppingId.price)}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xs text-gray-500">Lãi</div>
                            <div className={`font-medium ${recipe.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {formatCurrency(recipe.profit)}
                              <span className="text-xs ml-1">
                                ({recipe.profitMargin?.toFixed(0)}%)
                              </span>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  
                  {/* Desktop: horizontal icons */}
                  <div className="hidden sm:flex items-center gap-2">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDuplicate(recipe); }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded"
                      title="Sao chép"
                    >
                      <FiCopy size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); openModal(recipe); }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                      title="Sửa"
                    >
                      <FiEdit2 size={18} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setShowDeleteConfirm({ id: recipe._id, name: recipe.name }); }}
                      className="p-2 text-red-500 hover:bg-red-50 rounded"
                      title="Xóa"
                    >
                      <FiTrash2 size={18} />
                    </button>
                    {expandedId === recipe._id ? <FiChevronUp /> : <FiChevronDown />}
                  </div>
                </div>
                
                {/* Mobile Cost Display - aligned with image (outside flex container) */}
                {activeTab === 'dish' && !isSalesRole && (
                  <div className="sm:hidden flex gap-3 text-sm mt-2">
                    <span className="text-orange-600">Chi phí: {formatCurrency(recipe.calculatedCost)}</span>
                    {recipe.menuItemId && (
                      <span className={recipe.profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                        Lãi: {formatCurrency(recipe.profit)} ({recipe.profitMargin?.toFixed(0)}%)
                      </span>
                    )}
                  </div>
                )}
                {activeTab === 'topping' && !isSalesRole && (
                  <div className="sm:hidden flex gap-3 text-sm mt-2">
                    <span className="text-orange-600">Chi phí: {formatCurrency(recipe.calculatedCost)}</span>
                    {recipe.toppingId && (
                      <span className={recipe.profit >= 0 ? 'text-green-600' : 'text-red-600'}>
                        Lãi: {formatCurrency(recipe.profit)} ({recipe.profitMargin?.toFixed(0)}%)
                      </span>
                    )}
                  </div>
                )}
              </div>
              
              {/* Expanded Details */}
              {expandedId === recipe._id && (
                <div className="border-t px-4 py-3 bg-gray-50">
                  <div className="grid md:grid-cols-2 gap-4">
                    {/* Ingredients */}
                    <div>
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Nguyên liệu:</h4>
                      <ul className="space-y-1">
                        {recipe.ingredients.map((ing, idx) => (
                          <li key={idx} className="text-sm flex items-center gap-2">
                            <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                            {ing.ingredientName || ing.ingredientId?.name}: {formatAmount(ing.amount, ing.unit)} {recipeUnitLabels[ing.unit] || ing.unit}
                          </li>
                        ))}
                      </ul>
                      
                      {/* Supplies */}
                      {recipe.supplies?.length > 0 && (
                        <>
                          <h4 className="font-medium text-sm text-gray-700 mb-2 mt-3">Vật dụng:</h4>
                          <ul className="space-y-1">
                            {recipe.supplies.map((sup, idx) => (
                              <li key={idx} className="text-sm flex items-center gap-2">
                                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                                {sup.supplyName || sup.supplyId?.name}: {sup.amount} {recipeUnitLabels[sup.unit] || sup.unit}
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </div>
                    
                    {/* Steps */}
                    {recipe.steps && recipe.steps.length > 0 && (
                      <div>
                        <h4 className="font-medium text-sm text-gray-700 mb-2">Các bước:</h4>
                        <ol className="space-y-2">
                          {recipe.steps.map((step, idx) => (
                            <li key={idx} className="text-sm">
                              <div className="flex gap-2">
                                <span className="font-medium text-green-600">{step.stepNumber || idx + 1}.</span>
                                <div>
                                  <span className="font-medium">{step.title || step.description || step}</span>
                                  {step.details && (
                                    <p className="text-gray-500 text-xs mt-0.5">{step.details}</p>
                                  )}
                                </div>
                              </div>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}
                  </div>
                  
                  {/* Cost breakdown for dish */}
                  {activeTab === 'dish' && recipe.costBreakdown && !isSalesRole && (
                    <div className="mt-3 pt-3 border-t">
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Chi tiết chi phí:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {recipe.costBreakdown.ingredients?.map((item, idx) => (
                          <div key={`ing-${idx}`} className="bg-white p-2 rounded border">
                            <div className="text-gray-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                        {recipe.costBreakdown.brewing?.map((item, idx) => (
                          <div key={`brew-${idx}`} className="bg-purple-50 p-2 rounded border border-purple-200">
                            <div className="text-purple-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                        {recipe.costBreakdown.supplies?.map((item, idx) => (
                          <div key={`sup-${idx}`} className="bg-blue-50 p-2 rounded border border-blue-200">
                            <div className="text-blue-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Cost breakdown for topping */}
                  {activeTab === 'topping' && recipe.costBreakdown && !isSalesRole && (
                    <div className="mt-3 pt-3 border-t">
                      <h4 className="font-medium text-sm text-gray-700 mb-2">Chi tiết chi phí:</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        {recipe.costBreakdown.ingredients?.map((item, idx) => (
                          <div key={`ing-${idx}`} className="bg-white p-2 rounded border">
                            <div className="text-gray-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                        {recipe.costBreakdown.brewing?.map((item, idx) => (
                          <div key={`brew-${idx}`} className="bg-purple-50 p-2 rounded border border-purple-200">
                            <div className="text-purple-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                        {recipe.costBreakdown.supplies?.map((item, idx) => (
                          <div key={`sup-${idx}`} className="bg-blue-50 p-2 rounded border border-blue-200">
                            <div className="text-blue-600">{item.name}</div>
                            <div className="font-medium">{formatCurrency(item.totalCost)}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {recipe.notes && (
                    <div className="mt-3 pt-3 border-t">
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Ghi chú:</span> {recipe.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        )}
        
        {/* Loading more indicator */}
        {loadingMore && (
          <div className="flex justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600"></div>
          </div>
        )}
        
        {/* Load more trigger */}
        {!loadingMore && hasMore && (
          <div ref={observerTarget} className="h-10" />
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4 pb-20 md:pb-4">
          <div className="bg-white rounded-lg w-full max-w-2xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b flex justify-between items-center sticky top-0 bg-white z-10">
              <h2 className="text-lg font-bold">
                {editingRecipe ? 'Sửa công thức' : `Thêm công thức ${form.type === 'brewing' ? 'ủ' : form.type === 'topping' ? 'topping' : 'món'}`}
              </h2>
              <button onClick={closeModal} className="p-1 hover:bg-gray-100 rounded">
                <FiX size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-1">Tên công thức *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder={form.type === 'brewing' ? 'VD: Siro đường nâu' : 'VD: Trà sữa trân châu'}
                />
              </div>

              {/* Menu Item Selection (dish only) */}
              {form.type === 'dish' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Gán cho món</label>
                  <ProductSearchSelect
                    useApi={true}
                    apiParams={editingRecipe ? { includeAll: 'true' } : {}}
                    value={form.menuItemId}
                    initialProduct={selectedMenuItem ? {
                      id: selectedMenuItem._id || selectedMenuItem.id,
                      name: selectedMenuItem.name,
                      price: selectedMenuItem.price,
                      category: selectedMenuItem.category,
                      image: selectedMenuItem.image,
                      customOptions: selectedMenuItem.customOptions
                    } : null}
                    onChange={(product) => {
                      if (product) {
                        setSelectedMenuItem(product);
                        setForm(prev => ({ ...prev, menuItemId: product.id || product._id, sizeOption: '' }));
                      } else {
                        setSelectedMenuItem(null);
                        setForm(prev => ({ ...prev, menuItemId: '', sizeOption: '' }));
                      }
                    }}
                    placeholder="Nhập tên món để tìm..."
                    showImage={false}
                    showCategory={false}
                    showSelectedBadge={true}
                    maxResults={10}
                    notFoundText="Không tìm thấy món"
                    formatPrice={formatCurrency}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Nhập tên món trong menu để tính chi phí và lãi
                  </p>
                  
                  {/* Size Option Selection */}
                  {selectedMenuItem && selectedMenuItem.customOptions && selectedMenuItem.customOptions.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <label className="block text-sm font-medium mb-1 text-blue-800">
                        Công thức cho size cụ thể (tùy chọn)
                      </label>
                      <select
                        value={form.sizeOption}
                        onChange={(e) => setForm(prev => ({ ...prev, sizeOption: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Mặc định (tất cả size)</option>
                        {selectedMenuItem.customOptions.map((opt, idx) => (
                          <option key={idx} value={opt.name}>
                            {opt.name} - {formatCurrency(opt.price)}
                          </option>
                        ))}
                      </select>
                      <p className="text-xs text-blue-600 mt-1">
                        Để trống nếu công thức áp dụng cho tất cả size. Chọn size cụ thể nếu định lượng khác nhau.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Topping Selection (topping only) */}
              {form.type === 'topping' && (
                <div>
                  <label className="block text-sm font-medium mb-1">Gán cho topping *</label>
                  <select
                    value={form.toppingId}
                    onChange={(e) => {
                      const topping = availableToppings.find(t => t._id === e.target.value);
                      setSelectedTopping(topping || null);
                      setForm(prev => ({ ...prev, toppingId: e.target.value }));
                    }}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="">-- Chọn topping --</option>
                    {availableToppings.map(topping => (
                      <option key={topping._id} value={topping._id}>
                        {topping.name} - {formatCurrency(topping.price)}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Chọn topping để tính chi phí khi bán kèm món
                  </p>
                </div>
              )}

              {/* Output for brewing */}
              {form.type === 'brewing' && (
                <div className="p-3 bg-purple-50 border border-purple-200 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-purple-800">Kết quả thành phẩm *</label>
                    <div className="flex gap-2 items-center">
                      <span className="text-sm">Công thức này tạo ra</span>
                      <input
                        type="number"
                        value={form.outputAmount}
                        onChange={(e) => setForm(prev => ({ ...prev, outputAmount: parseFloat(e.target.value) || 0 }))}
                        className="w-24 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                        min="0"
                        step="0.01"
                      />
                      <select
                        value={form.outputUnit}
                        onChange={(e) => setForm(prev => ({ ...prev, outputUnit: e.target.value }))}
                        className="px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="ml">Mililit</option>
                        <option value="liter">Lít</option>
                        <option value="g">Gam</option>
                        <option value="kg">Kg</option>
                      </select>
                      <span className="text-sm">thành phẩm</span>
                    </div>
                  </div>
                  
                  {/* Auto discard option */}
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.autoDiscardEndOfDay}
                      onChange={(e) => setForm(prev => ({ ...prev, autoDiscardEndOfDay: e.target.checked }))}
                      className="mt-1 w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                    />
                    <div>
                      <span className="font-medium text-sm text-purple-800">Hủy lượng thừa cuối ngày</span>
                      <p className="text-xs text-purple-600">
                        Tồn kho còn lại sẽ tự động bị hủy vào cuối ngày (23:55) và ghi nhận vào Hàng hủy
                      </p>
                    </div>
                  </label>
                </div>
              )}

              {/* Ingredients */}
              <div>
                <label className="block text-sm font-medium mb-2">Nguyên liệu *</label>
                
                <div className="flex flex-wrap gap-2 mb-2">
                  <div className="flex-1 min-w-[180px]">
                    <ItemSearchSelect
                      itemType="ingredient"
                      items={form.type === 'brewing' ? availableIngredients : []}
                      groupedItems={(form.type === 'dish' || form.type === 'topping') ? {
                        'Nguyên liệu trực tiếp': dishIngredients.direct,
                        'Từ công thức ủ': dishIngredients.brewing
                      } : null}
                      value={selectedIngredient.ingredientId}
                      onChange={(item) => {
                        if (item) {
                          const unit = getIngredientUnit(item._id);
                          setSelectedIngredient(prev => ({ ...prev, ingredientId: item._id, unit }));
                        } else {
                          setSelectedIngredient(prev => ({ ...prev, ingredientId: '', unit: 'g' }));
                        }
                      }}
                      placeholder="Tìm nguyên liệu..."
                      showUnit={false}
                    />
                  </div>
                  <input
                    type="number"
                    value={selectedIngredient.amount}
                    onChange={(e) => setSelectedIngredient(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
                    className="w-16 px-2 py-2 border rounded-lg text-sm"
                    placeholder="SL"
                    min="0"
                    step="0.01"
                  />
                  <span className="flex items-center text-sm text-gray-500">{selectedIngredient.unit || 'g'}</span>
                  <button
                    type="button"
                    onClick={addIngredient}
                    className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
                  >
                    <FiPlus />
                  </button>
                </div>
                
                {/* Fraction buttons for piece unit */}
                {selectedIngredient.unit === 'piece' && (
                  <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <span className="text-xs text-yellow-700 block mb-2">Chọn nhanh phân số:</span>
                    <div className="flex flex-wrap gap-1">
                      {[
                        { label: '1', value: 1 },
                        { label: '1/2', value: 0.5 },
                        { label: '1/3', value: 0.333 },
                        { label: '1/4', value: 0.25 },
                        { label: '1/5', value: 0.2 },
                        { label: '1/6', value: 0.167 },
                        { label: '1/7', value: 0.143 },
                        { label: '1/8', value: 0.125 },
                        { label: '2', value: 2 },
                        { label: '3', value: 3 },
                      ].map(fraction => (
                        <button
                          key={fraction.label}
                          type="button"
                          onClick={() => setSelectedIngredient(prev => ({ ...prev, amount: fraction.value }))}
                          className={`px-2 py-1 text-xs rounded border ${
                            selectedIngredient.amount === fraction.value
                              ? 'bg-yellow-500 text-white border-yellow-500'
                              : 'bg-white text-yellow-700 border-yellow-300 hover:bg-yellow-100'
                          }`}
                        >
                          {fraction.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                {form.ingredients.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {form.ingredients.map((ing, idx) => (
                      <div key={idx} className="p-2 flex items-center justify-between">
                        <span className="text-sm">
                          {ing.ingredientName}: <strong>{formatAmount(ing.amount, ing.unit)} {recipeUnitLabels[ing.unit]}</strong>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeIngredient(idx)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <FiX size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Supplies (dish and topping) */}
              {(form.type === 'dish' || form.type === 'topping') && (
                <div>
                  <label className="block text-sm font-medium mb-2">Vật dụng</label>
                  
                  <div className="flex flex-wrap gap-2 mb-3">
                    <div className="flex-1 min-w-[180px]">
                      <ItemSearchSelect
                        itemType="supply"
                        items={availableSupplies}
                        value={selectedSupply.supplyId}
                        onChange={(item) => {
                          if (item) {
                            setSelectedSupply(prev => ({ ...prev, supplyId: item._id }));
                          } else {
                            setSelectedSupply(prev => ({ ...prev, supplyId: '' }));
                          }
                        }}
                        placeholder="Tìm vật dụng..."
                        showUnit={false}
                      />
                    </div>
                    <input
                      type="number"
                      value={selectedSupply.amount}
                      onChange={(e) => setSelectedSupply(prev => ({ ...prev, amount: parseInt(e.target.value) || 1 }))}
                      className="w-16 px-2 py-2 border rounded-lg text-sm"
                      placeholder="SL"
                      min="1"
                    />
                    <button
                      type="button"
                      onClick={addSupply}
                      className="px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                    >
                      <FiPlus />
                    </button>
                  </div>
                  
                  {form.supplies.length > 0 && (
                    <div className="border rounded-lg divide-y">
                      {form.supplies.map((sup, idx) => (
                        <div key={idx} className="p-2 flex items-center justify-between">
                          <span className="text-sm">
                            <FiBox className="inline mr-1 text-blue-500" />
                            {sup.supplyName}: <strong>{sup.amount} cái</strong>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeSupply(idx)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Steps */}
              <div>
                <label className="block text-sm font-medium mb-2">Các bước chế biến</label>
                
                <div className="space-y-2 mb-3 p-3 bg-gray-50 rounded-lg">
                  <input
                    type="text"
                    value={newStep.title}
                    onChange={(e) => setNewStep(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    placeholder="Tên bước (VD: Chuẩn bị nguyên liệu)"
                  />
                  <textarea
                    value={newStep.details}
                    onChange={(e) => setNewStep(prev => ({ ...prev, details: e.target.value }))}
                    className="w-full px-3 py-2 border rounded-lg text-sm"
                    rows="2"
                    placeholder="Chi tiết bước (VD: Rửa sạch trái cây, cắt nhỏ...)"
                  />
                  <button
                    type="button"
                    onClick={addStep}
                    className="w-full py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm flex items-center justify-center gap-2"
                  >
                    <FiPlus size={16} /> Thêm bước
                  </button>
                </div>
                
                {form.steps.length > 0 && (
                  <div className="border rounded-lg divide-y">
                    {form.steps.map((step, idx) => (
                      <div key={idx} className="p-3 flex gap-2">
                        <span className="w-6 h-6 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0 mt-0.5">
                          {idx + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{step.title}</p>
                          {step.details && (
                            <p className="text-xs text-gray-500 mt-1">{step.details}</p>
                          )}
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <button
                            type="button"
                            onClick={() => moveStep(idx, -1)}
                            disabled={idx === 0}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <FiChevronUp size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveStep(idx, 1)}
                            disabled={idx === form.steps.length - 1}
                            className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                          >
                            <FiChevronDown size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeStep(idx)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <FiX size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium mb-1">Ghi chú</label>
                <textarea
                  value={form.notes}
                  onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                  placeholder="Ghi chú thêm..."
                />
              </div>

              {/* Submit */}
              <button
                type="submit"
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium"
              >
                {editingRecipe ? 'Cập nhật' : 'Tạo công thức'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <h3 className="text-lg font-bold mb-2">Xác nhận xóa</h3>
            <p className="text-gray-600 mb-4">
              Bạn có chắc muốn xóa công thức "<strong>{showDeleteConfirm.name}</strong>"?
              {activeTab === 'brewing' && (
                <span className="block mt-2 text-sm text-orange-600">
                  Lưu ý: Nguyên liệu được tạo từ công thức này cũng sẽ bị xóa.
                </span>
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                className="flex-1 py-2 border rounded-lg hover:bg-gray-50"
              >
                Hủy
              </button>
              <button
                onClick={() => handleDelete(showDeleteConfirm.id)}
                className="flex-1 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
              >
                Xóa
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecipesManagement;
